import { observer } from "mobx-react";
import { ActionIcon, Anchor, Group, Paper, Stack, Tabs, Text, TextInput } from "@mantine/core";
import { useState } from "react";
import { BsChevronContract, BsChevronExpand } from "react-icons/bs";
import { AiOutlineArrowDown, AiOutlineArrowUp } from "react-icons/ai";
import { PodcastListItem } from "./PodcastListItem";
import styles from "./styles/PodcastList.module.css";

interface PodcastListProps {
    podcasts: Podcast[];
    limit?: number;
    flush?: boolean;
    defaultSort?: string;
}

export const PodcastList = observer(({ podcasts = [], limit = -1, flush = false, defaultSort }: PodcastListProps) => {
    const [isExpand, setIsExpand] = useState(false);
    const [filter, setFilter] = useState("");

    const [order, setOrder] = useState<boolean>(true);
    const [sort, setSort] = useState<string>(defaultSort || "name");

    const pods: Podcast[] = [...podcasts];

    const normalizeSortText = (value: string) => value.trim().replace(/^[^\p{L}\p{N}]+/u, "");

    const sortPodcastByNameDesc = (a: Podcast, b: Podcast) => {
        return normalizeSortText(a.name).localeCompare(normalizeSortText(b.name), "sv", {
            sensitivity: "base",
            numeric: true,
        });
    };

    const sortPodcastByNameAsc = (a: Podcast, b: Podcast) => {
        return normalizeSortText(b.name).localeCompare(normalizeSortText(a.name), "sv", {
            sensitivity: "base",
            numeric: true,
        });
    };

    const sortPodcastByDateDesc = (a: Podcast, b: Podcast) => {
        return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
    };

    const sortPodcastByDateAsc = (a: Podcast, b: Podcast) => {
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    };

    const sortPodcastByAuthorDesc = (a: Podcast, b: Podcast) => {
        return normalizeSortText(a.author.name).localeCompare(normalizeSortText(b.author.name), "sv", {
            sensitivity: "base",
            numeric: true,
        });
    };

    const sortPodcastByAuthorAsc = (a: Podcast, b: Podcast) => {
        return normalizeSortText(b.author.name).localeCompare(normalizeSortText(a.author.name), "sv", {
            sensitivity: "base",
            numeric: true,
        });
    };

    const sortPodcastByEpisodeCountDesc = (a: Podcast, b: Podcast) => {
        if (!a.episodes_count || !b.episodes_count) {
            return 0;
        }
        return a.episodes_count - b.episodes_count;
    };

    const sortPodcastByEpisodeCountAsc = (a: Podcast, b: Podcast) => {
        if (!a.episodes_count || !b.episodes_count) {
            return 0;
        }
        return b.episodes_count - a.episodes_count;
    };

    const onSort = (sort: string) => {
        setSort(sort);
    };

    const toggleSortOrder = () => {
        setOrder((current) => !current);
    };

    const sorting: SortCollection = {
        name: {
            sort: order ? sortPodcastByNameDesc : sortPodcastByNameAsc,
            label: "Namn",
        },
        date: {
            sort: order ? sortPodcastByDateAsc : sortPodcastByDateDesc,
            label: "Tillagd",
        },
        author: {
            sort: order ? sortPodcastByAuthorDesc : sortPodcastByAuthorAsc,
            label: "Utgivare",
            prop: "author",
        },
        episodes: {
            sort: order ? sortPodcastByEpisodeCountDesc : sortPodcastByEpisodeCountAsc,
            label: "Episoder",
            prop: "episodes_count",
        },
    };

    if (sorting.hasOwnProperty(sort)) {
        pods.sort(sorting[sort].sort);
    }

    const sortOrderLabel =
        sort === "date"
            ? order
                ? "Nyast"
                : "Äldst"
            : sort === "episodes"
              ? order
                  ? "Färre"
                  : "Flest"
              : order
                ? "A-Ö"
                : "Ö-A";

    const sortOrderAriaLabel =
        sort === "date"
            ? order
                ? "nyast först"
                : "äldst först"
            : sort === "episodes"
              ? order
                  ? "färre först"
                  : "flest först"
              : order
                ? "A till Ö"
                : "Ö till A";

    const sortOrderIcon = order ? <AiOutlineArrowDown /> : <AiOutlineArrowUp />;

    const showExpand = limit > 0 && podcasts.length > limit + 10 && filter === "";
    return (
        <Stack gap={flush ? 0 : "xs"}>
            {podcasts.length > 10 && (
                <Paper key={"filter-podcast-list"} p={flush ? 0 : "md"} radius="md" withBorder={!flush}>
                    <div className={styles.filter}>
                        <TextInput
                            onChange={(event) => {
                                setFilter(event.currentTarget.value);
                            }}
                            value={filter}
                            type="search"
                            placeholder="Sök i listan..."
                        />
                    </div>
                </Paper>
            )}
            {podcasts.length > 4 && (
                <Paper p={flush ? 0 : "md"} radius="md" withBorder={!flush}>
                    <div className={styles.sort}>
                        <Group className={styles.sortControls} justify="space-between" align="center" gap="sm">
                            <Tabs value={sort} onChange={(value) => value && onSort(value)}>
                                <Tabs.List>
                                    {Object.keys(sorting).map((key) => {
                                        const currentSort = sorting[key];
                                        const shouldShow =
                                            currentSort.prop && pods.length > 0
                                                ? pods[0].hasOwnProperty(currentSort.prop)
                                                : true;
                                        return (
                                            shouldShow && (
                                                <Tabs.Tab value={key} key={key}>
                                                    {sorting[key].label}
                                                </Tabs.Tab>
                                            )
                                        );
                                    })}
                                </Tabs.List>
                            </Tabs>
                            <ActionIcon
                                aria-label={`Växla sorteringsordning, nu ${sortOrderAriaLabel}`}
                                className={styles.orderToggle}
                                onClick={toggleSortOrder}
                                variant="default">
                                {sortOrderIcon}
                            </ActionIcon>
                            <Text c="dimmed" size="sm">
                                {sortOrderLabel}
                            </Text>
                        </Group>
                    </div>
                </Paper>
            )}
            {pods.map((pod: Podcast, index: number) => {
                if (limit > 0 && index > limit && showExpand && !isExpand) {
                    return null;
                }

                if (filter.length > 0) {
                    if (pod.name.toLowerCase().indexOf(filter.toLowerCase()) === -1) {
                        return null;
                    }
                }

                return <PodcastListItem key={"podcast-list-item-" + index} podcast={pod} />;
            })}
            {showExpand && (
                <Anchor
                    onClick={(event) => {
                        event.preventDefault();
                        setIsExpand(!isExpand);
                    }}
                    className={styles.expand}
                    href="#expand"
                    underline="never">
                    <Group gap="xs" wrap="nowrap">
                        {isExpand ? <BsChevronContract /> : <BsChevronExpand />}
                        <Text component="span" size="sm">
                            {isExpand ? "Dölj" : `Visa alla ${podcasts.length} poddar...`}
                        </Text>
                    </Group>
                </Anchor>
            )}
        </Stack>
    );
});
